import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type { DilemmaDocument } from "@kekkle/shared/types/documents/dilemma-document/dilemma-document";
import {
  subscribeToDilemmas as subscribeToDilemmasService,
  addDilemma as addDilemmaService,
  deleteDilemma as deleteDilemmaService,
  voteOnDilemma as voteOnDilemmaService,
  revoteOnDilemma as revoteOnDilemmaService,
} from "@/helpers/firestore/DilemmaService";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import type { FirestoreError } from "firebase/firestore";

export const useDilemmasStore = defineStore("dilemmas", () => {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  /* State */
  const dilemmasByGroup = ref<Record<string, DilemmaDocument[]>>({});
  const loadingByGroup = ref<Record<string, boolean>>({});
  const fetchError = ref<string | null>(null);
  const recentlyVotedIds = ref<Set<string>>(new Set());

  /* Listener management */
  let activeUnsubscribe: (() => void) | null = null;
  let subscribedGroupId: string | null = null;

  /* Computed */
  const activeGroupId = computed(() => userStore.activeGroup);

  const allDilemmas = computed(
    () =>
      (activeGroupId.value && dilemmasByGroup.value[activeGroupId.value]) || [],
  );

  const unansweredDilemmas = computed(() => {
    return allDilemmas.value.filter(
      (d) =>
        !d.participants?.includes(authStore.authUuid as string) ||
        recentlyVotedIds.value.has(d._id),
    );
  });

  const answeredDilemmas = computed(() => {
    return allDilemmas.value.filter((d) =>
      d.participants?.includes(authStore.authUuid as string),
    );
  });

  const nextUnansweredDilemma = computed(() => {
    return unansweredDilemmas.value.length > 0
      ? unansweredDilemmas.value[0]
      : null;
  });

  const loadingInitialDilemmas = computed(() => {
    if (!activeGroupId.value) return false;
    return loadingByGroup.value[activeGroupId.value] ?? false;
  });

  /* Listener lifecycle */
  function subscribe(groupId: string): void {
    if (subscribedGroupId === groupId) return;
    unsubscribe();

    if (!dilemmasByGroup.value[groupId]?.length) {
      loadingByGroup.value[groupId] = true;
    }
    fetchError.value = null;
    activeUnsubscribe = subscribeToDilemmasService(groupId, {
      onDilemmas: (dilemmas) => {
        dilemmasByGroup.value[groupId] = dilemmas;
        loadingByGroup.value[groupId] = false;
      },
      onError: (error) => {
        fetchError.value = handleFirestoreException({
          e: error as FirestoreError,
        });
        loadingByGroup.value[groupId] = false;
      },
    });
    subscribedGroupId = groupId;
  }

  function unsubscribe(): void {
    if (activeUnsubscribe) {
      activeUnsubscribe();
      activeUnsubscribe = null;
    }
    subscribedGroupId = null;
  }

  function reset(): void {
    unsubscribe();
    dilemmasByGroup.value = {};
    loadingByGroup.value = {};
  }

  function subscribeToDilemmas(): void {
    const groupId = activeGroupId.value;
    if (!groupId) return;
    subscribe(groupId);
  }

  /* Actions */
  async function addDilemma(data: Omit<DilemmaDocument, "_id">): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) throw new Error("No active group");
    await addDilemmaService(groupId, data);
  }

  async function deleteDilemma(dilemmaId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) throw new Error("No active group");
    await deleteDilemmaService(groupId, dilemmaId);
  }

  function clearRecentlyVoted(): void {
    recentlyVotedIds.value.clear();
  }

  async function voteOnDilemma(
    dilemmaId: string,
    selectedOption: string,
    isAnonymous: boolean,
  ): Promise<void> {
    const groupId = activeGroupId.value;
    const userId = authStore.authUuid;
    if (!groupId || !userId) throw new Error("No active group or user");

    // Retain visibility in unanswered list for 3 seconds so vote result is visible
    recentlyVotedIds.value.add(dilemmaId);
    setTimeout(() => {
      recentlyVotedIds.value.delete(dilemmaId);
    }, 3000);

    await voteOnDilemmaService(
      groupId,
      dilemmaId,
      userId,
      selectedOption,
      isAnonymous,
    );
  }

  async function revoteOnDilemma(
    dilemmaId: string,
    newSelectedOption: string,
  ): Promise<void> {
    const groupId = activeGroupId.value;
    const userId = authStore.authUuid;
    if (!groupId || !userId) throw new Error("No active group or user");
    await revoteOnDilemmaService(groupId, dilemmaId, userId, newSelectedOption);
  }

  return {
    // State & Computed
    allDilemmas,
    unansweredDilemmas,
    answeredDilemmas,
    nextUnansweredDilemma,
    loadingInitialDilemmas,
    fetchError,

    // Lifecycle
    subscribeToDilemmas,
    unsubscribe,
    reset,
    clearRecentlyVoted,

    // Actions
    addDilemma,
    deleteDilemma,
    voteOnDilemma,
    revoteOnDilemma,
  };
});
